import React, { useCallback, useMemo, useState } from 'react';
import {
  Badge, Button,
} from 'react-bootstrap';
import {
  AiOutlineDelete, AiOutlineMenu,
} from 'react-icons/ai';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { FiDownload } from 'react-icons/fi';
import { ImAttachment } from 'react-icons/im';
import { IoEyeOutline } from 'react-icons/io5';
import TextColumn from 'components/EditableTable/TextColumn';
import DateColumn from 'components/EditableTable/DateColumn';
import DropdownColumn from 'components/EditableTable/DropdownColumn';
import TableEdit from 'components/EditableTable/Table';
import Checkbox from 'components/Form/FormCheckbox';
import DocDetailOffcanvas from 'components/DocsManagment/DetailOffcanvas';
import useModal from 'hooks/useModal';
import { DocNotePayload, DocRequestEntity, DocRequestStatus } from 'types/DocRequest';
import DocStatusBadge from 'components/DocsManagment/StatusBadge';
import DocsRequestApi from 'services/api/DocsRequest';
import handlePromise from 'utils/Promise';
import { useParams } from 'react-router';
import classNames from 'classnames';
import moment from 'moment';
import { isValidDate } from 'utils/date';
import UploadDocModal from 'components/DocsManagment/UploadDocModal';
import DocNoteModal from 'components/DocsManagment/NoteModal';

type DocsTableEditProps = {
  data?: DocRequestEntity[]
  reloadData: ( newData: DocRequestEntity ) => void
  handleViewDocument: (
    documentoId: number,
    cuentaDeInversionID: number,
    url: string,
    nombreArchivo: string,
    tipoArchivo: string,
    descargar: boolean,
  ) => void;
};

function DocsTableEdit( { data, reloadData, handleViewDocument }: DocsTableEditProps ) {
  const { id } = useParams();
  const [loading, setLoading] = useState( false );
  const [reloaded, setReloaded] = useState( new Date().valueOf() );

  const {
    showModal: showCanvas,
    closeModal: closeCanvas,
    openModal: openCanvas,
    selectedItem,
  } = useModal<DocRequestEntity>();

  const {
    showModal: showNoteModal,
    closeModal: closeNoteModal,
    openModal: openNoteModal,
    selectedItem: selectedItemUpdate,
  } = useModal<DocNotePayload>();

  const {
    showModal: showUploadFileModal,
    closeModal: closeUploadFileModal,
    openModal: openUploadFileModal,
    selectedItem: itemUploadFile,
  } = useModal<DocRequestEntity>();

  const submitForm = useCallback( async (
    docId: number,
    field: string,
    value?: string | boolean,
    note?: string,
  ) => {
    if ( ( typeof value === 'string' && value ) || typeof value === 'boolean' ) {
      setLoading( true );
      const dataToSend = {
        nota: note,
        solicitudData: {
          [field]: value,
        },
      };

      const [, response, responseData] = await handlePromise(
        DocsRequestApi.editDocRequests( docId, dataToSend ),
      );

      if ( response.ok ) {
        reloadData( responseData );
      }
      setLoading( false );
    // ToDo show error
    }
  }, [id] );

  const statuses = useMemo( () => Object.values( DocRequestStatus ).map( ( status ) => ( {
    id: status,
    label: <DocStatusBadge status={status} />,
    icon: <DocStatusBadge status={status} showLabel={false} />,
  } ) ), [] );

  if ( !data?.length ) return null;

  return (
    <>
      <TableEdit>
        <thead>
          <tr>
            <th>ID</th>
            <th style={{ minWidth: 190 }}>Nombre</th>
            <th>Descripción</th>
            <th
              className="text-center"
              style={{ width: 120 }}
            >
              Últ. Rev.
            </th>
            <th
              className="text-center"
              style={{ width: 120 }}
            >
              F. Límite
            </th>
            <th
              className="text-center"
              style={{ width: 110 }}
            >
              Estado
            </th>
            <th
              className="text-center"
              style={{ width: 120 }}
            >
              F. Expiración
            </th>
            <th style={{ width: 160 }} />
            <th style={{ width: 60 }} />
          </tr>
        </thead>

        <tbody className={classNames( {
          'app-component-loading': loading,
        } )}
        >
          {data?.map( ( item ) => (
            <tr key={item.id}>
              <td>{item.id}</td>
              <td>
                <div className="text-truncate-2">
                  <TextColumn
                    value={item.nombre}
                    onChange={( value ) => submitForm( item.id, 'nombre', value )}
                  />
                </div>
              </td>
              <td>
                <div className="text-truncate-2">
                  <TextColumn
                    value={item.descripcion}
                    onChange={( value ) => submitForm( item.id, 'descripcion', value )}
                  />
                </div>
              </td>
              <td className="text-center">
                <span className="text--black10">
                  {isValidDate( item.fechaUltimaRevision ) ? moment( item.fechaUltimaRevision ).format( 'DD/MM/YYYY' ) : 'N/A'}
                </span>
              </td>
              <td className="text-center">
                <DateColumn
                  value={item.fechaLimite}
                  onChange={( value ) => submitForm( item.id, 'fechaLimite', value )}
                  className="text--black10"
                />
              </td>
              <td>
                <DropdownColumn
                  key={reloaded}
                  value={item.estado}
                  onChange={( value ) => openNoteModal( {
                    ...item,
                    newValue: value as DocRequestStatus,
                    field: 'estado',
                  } )}
                  options={statuses}
                  placeholder={(
                    <Badge bg="light">
                      <IoMdInformationCircleOutline size={11} />
                    </Badge>
                )}
                />
              </td>
              <td className="text-center">
                <DateColumn
                  key={reloaded}
                  value={item.fechaExpiracion}
                  onChange={( value ) => openNoteModal( {
                    ...item,
                    newValue: value,
                    field: 'fechaExpiracion',
                  } )}
                  className="text--black10"
                />
              </td>
              <td className="text-center">
                <Button
                  className="px-1 py-0"
                  variant="link"
                  onClick={() => openCanvas( item )}
                >
                  <AiOutlineMenu />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                  disabled={!( !!item.nombreArchivo && !!item.urlArchivo )}
                  onClick={() => {
                    if ( !item.id || !item.urlArchivo || !item.nombreArchivo
                      || !item.tipoArchivo
                    ) return;
                    handleViewDocument(
                      item.id,
                      item.cuentaDeInversionID,
                      item.urlArchivo,
                      item.nombreArchivo,
                      item.tipoArchivo,
                      false,
                    );
                  }}
                >
                  <IoEyeOutline />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                  onClick={() => openUploadFileModal( item )}
                >
                  <ImAttachment className="text--md" />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                  disabled={!( !!item.nombreArchivo && !!item.urlArchivo )}
                  onClick={() => {
                    if ( !item.id || !item.urlArchivo || !item.nombreArchivo
                      || !item.tipoArchivo
                    ) return;
                    handleViewDocument(
                      item.id,
                      item.cuentaDeInversionID,
                      item.urlArchivo,
                      item.nombreArchivo,
                      item.tipoArchivo,
                      true,
                    );
                  }}
                >
                  <FiDownload />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                >
                  <AiOutlineDelete />
                </Button>
              </td>
              <td className="text-end pe-0">
                <Checkbox
                  showErrors={false}
                  value={item.activo}
                  type="switch"
                  onChange={( value ) => submitForm( item.id, 'activo', value )}
                  classNames={{
                    formGroup: 'mb-0 mt-1',
                  }}
                />
              </td>
            </tr>
          ) )}
        </tbody>
      </TableEdit>

      {showCanvas && (
      <DocDetailOffcanvas
        onClose={closeCanvas}
        data={selectedItem}
        handleViewDocument={handleViewDocument}
      />
      )}
      {showUploadFileModal && (
      <UploadDocModal
        data={itemUploadFile}
        onClose={closeUploadFileModal}
        reloadData={reloadData}
      />
      )}
      {showNoteModal && (
      <DocNoteModal
        onSubmit={( { nota } ) => {
          if ( selectedItemUpdate?.id ) {
            closeNoteModal();
            submitForm(
              selectedItemUpdate.id,
              selectedItemUpdate.field,
              selectedItemUpdate.newValue,
              nota,
            );
          }
        }}
        onDismissChange={() => {
          setReloaded( new Date().valueOf() );
          closeNoteModal();
        }}
      />
      )}
    </>
  );
}

export default DocsTableEdit;
